import React from 'react'
import { Box, Paper, Skeleton, Stack, Typography } from '@mui/material'
import ViewportAheadCard from './ViewportAheadCard'

interface DashboardGridItem {
  id: string
  column?: number
  columnOrder?: number
  itemsPerPage?: number
  collapsed?: boolean
}

export default function DashboardGrid<T extends DashboardGridItem>({
  items,
  columns,
  renderItem,
  virtualize = false,
  getItemTitle,
}: {
  items: T[]
  columns: number
  renderItem: (item: T) => React.ReactNode
  virtualize?: boolean
  getItemTitle?: (item: T) => string
}) {
  const resolved = Math.min(4, Math.max(1, Math.floor(columns)))

  const stacks: any[][] = React.useMemo(() => {
    const next = Array.from({ length: resolved }, () => [] as any[])

    const normalized = items.map((item, idx) => {
      const normalizedColumn = (() => {
        const col = item.column
        if (typeof col === 'number' && Number.isFinite(col)) {
          return Math.min(resolved, Math.max(1, Math.floor(col)))
        }
        return (idx % resolved) + 1
      })()

      const normalizedOrder = (() => {
        const ord = item.columnOrder
        if (typeof ord === 'number' && Number.isFinite(ord) && ord >= 1) {
          return Math.floor(ord)
        }
        return Number.MAX_SAFE_INTEGER - (items.length - idx)
      })()

      return { ...item, _column: normalizedColumn, _order: normalizedOrder, _idx: idx }
    })

    for (let col = 1; col <= resolved; col += 1) {
      const inCol = normalized
        .filter((item) => item._column === col)
        .sort((a, b) => {
          if (a._order === b._order) {
            return a._idx - b._idx
          }
          return a._order - b._order
        })
      next[col - 1] = inCol
    }

    return next
  }, [items, resolved])

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        width: '100%',
      }}
    >
      {stacks.map((col, colIdx) => (
        <Box
          key={`col-${colIdx}`}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            flex: 1,
            minWidth: 0,
          }}
        >
          {col.map((item) => {
            const title = getItemTitle?.(item) ?? 'Prep card'
            const estimatedHeight = item.collapsed
              ? 54
              : 106 + Math.min(15, Math.max(3, item.itemsPerPage ?? 5)) * 72
            const placeholder = (
              <Paper
                variant="outlined"
                aria-label={`${title} is prepared and will display as you approach it`}
                sx={{
                  width: '100%',
                  minHeight: estimatedHeight,
                  boxSizing: 'border-box',
                  px: 2,
                  py: 1,
                  borderRadius: 3,
                  overflow: 'hidden',
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                  <Typography component="h3" variant="h6" noWrap sx={{ fontWeight: 600 }}>
                    {title}
                  </Typography>
                  <Skeleton variant="circular" width={28} height={28} />
                </Stack>
                {item.collapsed ? null : (
                  <Stack spacing={1} sx={{ mt: 2 }} aria-hidden="true">
                    <Skeleton variant="rounded" height={34} />
                    <Skeleton variant="rounded" height={34} width="92%" />
                    <Skeleton variant="rounded" height={34} width="85%" />
                  </Stack>
                )}
              </Paper>
            )

            return (
              <Box key={item.id} sx={{ width: '100%', minWidth: 0 }}>
                <ViewportAheadCard
                  cardId={item.id}
                  estimatedHeight={estimatedHeight}
                  placeholder={placeholder}
                  renderCard={() => renderItem(item)}
                  enabled={virtualize}
                />
              </Box>
            )
          })}
        </Box>
      ))}
    </Box>
  )
}
